import { useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useI18n } from '../../i18n';
import { useSessionContext } from '../../contexts/SessionContext';
import { useJournalContext } from '../../contexts/JournalContext';
import { useSheetContext } from '../../contexts/SheetContext';
import { useLayoutContext } from '../../contexts/LayoutContext';
import { usePageOpsContextOptional } from '../../contexts/PageOpsContext';
import { useCanvasContext } from '../../contexts/CanvasContext';
import { usePlanViewMemory } from '../studio/studioWorkspaceMemory';
import { useSessionStart } from '../../hooks/useCampaignSessions';
import { monsterLabel } from '../studio/StudioBestiary';
import { useSrdIndex } from '../../lib/search/srdIndex';
import { requestOpen } from '../../lib/search/openRequest';
import type { SidebarTabId } from '../../types';
import { SearchPalette, type SearchEntry } from './SearchPalette';
import { buildSearchEntries } from './searchSources';
import { useRevealAsk } from './useRevealAsk';
import { SrdArticleDialog, type SrdArticleTarget } from './SrdArticleDialog';

/**
 * Ctrl+K at the table — the same search as in Подготовка, for the GM and for a player.
 *
 * A player's list is built from what their client holds (the server's per-player projection) and filtered again by
 * the same rules (searchSources.ts): no beats, only the players' map, only their sheets, only readable entries, only
 * visible chronicle events; the SRD names come from /api/srd/index (reference data). A player has no commands that
 * change the game here.
 *
 * Opening: an entry — the GM's editor window, a player's journal preview; a map — «Открыть у себя» for the GM (the
 * players are moved only by «Показать игрокам…», the strip's own confirmation); a beat — Подготовка on it; a sheet —
 * the sheet; an event — the letopis on it; an SRD thing — its article.
 */
export function TableSearch(props: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly openSheet: (sheetId: string) => void;
  readonly openJournalEditor: (entryId: string) => void;
  readonly setActiveTab: (tab: SidebarTabId) => void;
}): JSX.Element {
  const { open, onClose, openSheet, openJournalEditor, setActiveTab } = props;
  const { t, locale } = useI18n();
  const { mp, pages, isGM, outgoingPageId } = useSessionContext();
  const { entries } = useJournalContext();
  const { sheets: sheetsApi } = useSheetContext();
  const layout = useLayoutContext();
  const pageOps = usePageOpsContextOptional();
  const { engine } = useCanvasContext();
  const planMemory = usePlanViewMemory(`${mp.participant?.id ?? ''}@${mp.session?.id ?? ''}`);
  const sessionStart = useSessionStart(mp.serverState ?? null, mp.send);
  const reveal = useRevealAsk(mp.send);
  const srd = useSrdIndex(open);
  const [article, setArticle] = useState<SrdArticleTarget | null>(null);

  // The GM's «token / + sheet / На карту»: on the map the GM looks at (as the SRD panel does), never the players' by itself.
  const spawn = (slug: string, withSheet: boolean, name: string): void => {
    if (!mp.session) return;
    const gs = engine.gridSize;
    mp.send({ type: 'srd:spawn-monster', slug, x: 5 * gs + gs / 2, y: 5 * gs + gs / 2, withSheet, name, pageId: outgoingPageId });
  };
  // The handlers are read at the moment of a pick: the rows are rebuilt when the DATA changes, not on every render.
  const live = useRef({ spawn, openSheet, openJournalEditor, setActiveTab, layout, pageOps, planMemory, sessionStart, reveal });
  live.current = { spawn, openSheet, openJournalEditor, setActiveTab, layout, pageOps, planMemory, sessionStart, reveal };
  const hasPageOps = pageOps !== null;

  const list = useMemo((): SearchEntry[] => {
    if (!open) return [];
    const tab = (id: SidebarTabId, key: Parameters<typeof t>[0]): SearchEntry => ({ id: `tab-${id}`, kind: 'action', label: t(key), run: () => live.current.setActiveTab(id) });
    const commands: SearchEntry[] = isGM
      ? [
          { id: 'to-prep', kind: 'action', label: t('search.cmd.toPrep'), run: () => live.current.layout.dispatch({ type: 'layout:toggleStudio' }) },
          {
            id: 'create-beat',
            kind: 'action',
            label: `＋ ${t('studio.palette.createBeat')}`,
            // Made in Подготовка: the request waits there for the surface to mount.
            run: () => {
              requestOpen({ kind: 'studio-action', id: 'create-beat' });
              live.current.layout.dispatch({ type: 'layout:toggleStudio' });
            },
          },
          ...(sessionStart.running
            ? []
            : [{ id: 'start-session', kind: 'action' as const, label: t('timeline.session.start'), run: () => live.current.sessionStart.start() }]),
          tab('plan', 'search.cmd.openPlan'),
          tab('journal', 'search.cmd.openJournal'),
          tab('map', 'search.cmd.openMaps'),
          tab('timeline', 'search.cmd.openChronicle'),
          tab('srd', 'search.cmd.openRules'),
        ]
      : [
          tab('journal', 'search.cmd.openJournal'),
          tab('map', 'search.cmd.openMaps'),
          tab('timeline', 'search.cmd.openChronicle'),
          tab('srd', 'search.cmd.openRules'),
        ];
    const players = pages.activePageId;
    return buildSearchEntries({
      t,
      locale,
      viewer: { isGM, participantId: mp.participant?.id ?? '', name: mp.participant?.name ?? '', userId: mp.participant?.userId ?? undefined },
      entries,
      pages: pages.pages,
      beats: mp.serverState?.beats ?? [],
      sheets: sheetsApi.sheets,
      timeline: mp.serverState?.timeline ?? [],
      srd,
      context: {
        beatIds: mp.serverState?.currentBeatId ? [mp.serverState.currentBeatId] : [],
        openPageId: pages.viewingPageId,
        playersPageId: players,
      },
      open: {
        journal: (id) => {
          if (isGM) {
            live.current.openJournalEditor(id);
            return;
          }
          live.current.setActiveTab('journal');
          requestOpen({ kind: 'journal', id });
        },
        page: (id) => {
          const ops = live.current.pageOps;
          if (isGM && ops !== null) ops.pageCallbacks.preview(id);
          else live.current.setActiveTab('map');
        },
        ...(isGM
          ? {
              beat: (id: string) => {
                live.current.planMemory.requestOpenBeat(id);
                live.current.layout.dispatch({ type: 'layout:toggleStudio' });
              },
            }
          : {}),
        sheet: (id) => live.current.openSheet(id),
        timeline: (id) => {
          live.current.setActiveTab('timeline');
          requestOpen({ kind: 'timeline', id });
        },
        srd: (kind, slug) => setArticle({ kind, slug }),
      },
      actionsFor: isGM
        ? {
            // A GM-only entry: «Показать игрокам…» through the journal's reveal confirmation.
            journal: (e) => (e.visibility !== 'gm' ? [] : [{ id: 'reveal', label: t('search.act.reveal'), run: () => live.current.reveal.ask(e) }]),
            // The players' map is theirs already: no «Show» for it.
            page: (p) =>
              !hasPageOps || p.id === players
                ? []
                : [{ id: 'show', label: t('search.act.show'), run: () => live.current.pageOps?.pageCallbacks.askShow(p.id) }],
            monster: (m) => [{ id: 'to-map', label: t('search.act.toMap'), run: () => live.current.spawn(m.slug, true, monsterLabel(m.name, locale)) }],
          }
        : undefined,
      commands,
    });
  }, [open, isGM, t, locale, entries, pages.pages, pages.activePageId, pages.viewingPageId, mp.serverState?.beats, mp.serverState?.timeline, mp.serverState?.currentBeatId, mp.participant, sheetsApi.sheets, srd, hasPageOps, sessionStart.running]);

  return (
    <>
      <SearchPalette
        open={open}
        entries={list}
        t={t}
        onClose={onClose}
        placeholder={t(isGM ? 'search.placeholder.gm' : 'search.placeholder.player')}
        scopeHint={t(isGM ? 'search.emptyScope.gm' : 'search.emptyScope.player')}
        loading={open && srd === null}
        testId="table-search"
      />
      {reveal.dialog}
      <SrdArticleDialog
        target={article}
        onClose={() => setArticle(null)}
        onSpawn={isGM ? (slug, withSheet, name) => spawn(slug, withSheet, name ?? slug) : undefined}
      />
    </>
  );
}

export default TableSearch;
